import React from 'react';
import {
	ShowTypingState,
	isCommandMessageSurveyStatus,
} from '@enuan/chat__utils';
import classNames from 'classnames';
import { keyframes } from '@emotion/react';
import { useSelector } from 'react-redux';
import {
	getAgentTypingMessage,
	getFirstMessageStack,
} from '../../../reduxStore/selectors/messagesStack.selectors';
import { Message } from '../../UI/Message/Message';
import type { IMessageProps } from '../../UI/Message/Message';
import { useIntl } from 'react-intl';
import { chatFrameMessages } from './i18n';
import { isRenderableMessage } from '../../../utils/isRenderableMessage';
import { styled } from '@mui/material/styles';
import { useSenderName } from 'src/components/UI/Message/components/sender/hooks';
import type { IClientMessage } from '../../../interfaces/IClientMessage';

const wave = keyframes({
	'0%,60%,100%': {
		transform: 'initial',
	},

	'30%': {
		transform: 'scale(1.5)',
	},
});

export const WritingMessage = styled(
	({ className, ...props }: Omit<IMessageProps, 'message' | 'children'>) => {
		const firstMessage = useSelector(getFirstMessageStack);
		const message =
			firstMessage && isRenderableMessage(firstMessage)
				? firstMessage
				: undefined;
		const agentTypingMessage = useSelector(getAgentTypingMessage);

		const intl = useIntl();

		const isTyping =
			agentTypingMessage?._data.parameters.state !== ShowTypingState.CLEARED;
		const isSurveyStatus = message && isCommandMessageSurveyStatus(message);

		const isAgentTyping = agentTypingMessage && isTyping;

		const getSenderName = useSenderName();

		if (!(isAgentTyping || (message && !isSurveyStatus))) return null;

		const sourceMessage = (message ?? agentTypingMessage) as IClientMessage;
		const { sender } = sourceMessage;
		const state = agentTypingMessage
			? agentTypingMessage._data.parameters.state
			: ShowTypingState.COMPOSING;
		const typingText = intl.formatMessage(chatFrameMessages.typing, {
			sender: getSenderName(sender),
			state,
		});

		const customFooter = (
			<span className="message__typing">
				{typingText}
				<span className="dots">
					{Array.from({ length: 3 }).map((_, id) => (
						<span className="dot" key={id} />
					))}
				</span>
			</span>
		);

		return (
			<Message
				message={sourceMessage}
				className={classNames('chat__writing', className)}
				customHeader={null}
				customFooter={customFooter}
				customExtra={null}
				wrapBalloon={false}
				{...props}
			/>
		);
	},
	{
		target: 'WritingMessage-root',
		label: 'WritingMessage',
	},
)({
	gridTemplateColumns: 'auto 1fr auto',
	opacity: 0.7,
	'& .Message-avatar': {
		gridArea: 'bottom-left !important',
		marginBottom: 'unset !important',
		width: '15px',
		height: '15px',
		justifySelf: 'flex-end !important',
		alignSelf: 'flex-start !important',
		marginTop: '-0.2em',
	},
	'& .message__footer': {
		height: '15px',
		alignItems: 'flex-end',
		'& .dots': {
			marginLeft: '0.25em',
			display: 'inline',
			'& .dot': {
				width: '0.2em',
				height: '0.2em',
			},
		},
	},
	'& .dots .dot': {
		display: 'inline-block',
		width: '0.3em',
		height: '0.3em',
		borderRadius: '50%',
		marginRight: '0.2em',
		background: 'var(--message__dots)',
		animation: `${wave} 1s linear infinite`,
		transformOrigin: 'bottom center',
		'&:nth-child(2)': {
			animationDelay: '-0.8s',
		},
		'&:nth-child(3)': {
			animationDelay: '-0.6s',
		},
	},
});
